import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

const PaymentRequestsManagement: React.FC = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [filter, setFilter] = useState<'all' | 'Pending' | 'Approved' | 'Paid' | 'Rejected'>('Pending');

  useEffect(() => {
    loadPaymentRequests();
  }, []);

  const loadPaymentRequests = async () => {
    try {
      const response = await adminAPI.getAllPaymentRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load payment requests:', error);
    }
  };

  const handleApprove = async (requestId: string, partnerId: string) => {
    if (window.confirm('Approve this payment request?')) {
      try {
        await adminAPI.approvePaymentRequest(partnerId, requestId);
        alert('Payment request approved');
        loadPaymentRequests();
      } catch (error: any) {
        alert('Failed to approve: ' + (error.response?.data?.message || 'Unknown error'));
      }
    }
  };

  const handleReject = async (requestId: string, partnerId: string) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    if (reason !== null) {
      try {
        await adminAPI.rejectPaymentRequest(partnerId, requestId, reason);
        alert('Payment request rejected');
        loadPaymentRequests();
      } catch (error: any) {
        alert('Failed to reject: ' + (error.response?.data?.message || 'Unknown error'));
      }
    }
  };

  const handleMarkPaid = async (requestId: string, partnerId: string) => {
    if (window.confirm('Mark this payment as paid? This will update the partner\'s earnings.')) {
      try {
        await adminAPI.markPaymentPaid(partnerId, requestId);
        alert('Payment marked as paid');
        loadPaymentRequests();
      } catch (error: any) {
        alert('Failed to mark paid: ' + (error.response?.data?.message || 'Unknown error'));
      }
    }
  };

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(r => r.status === filter);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>💳 Payment Requests</h1>

      {/* Filter Tabs */}
      <div style={styles.tabs}>
        <button 
          onClick={() => setFilter('Pending')} 
          style={{...styles.tab, background: filter === 'Pending' ? '#fc8019' : '#e0e0e0'}}
        >
          ⏳ Pending ({requests.filter(r => r.status === 'Pending').length})
        </button>
        <button 
          onClick={() => setFilter('Approved')} 
          style={{...styles.tab, background: filter === 'Approved' ? '#5d8ed5' : '#e0e0e0'}}
        >
          ✅ Approved ({requests.filter(r => r.status === 'Approved').length})
        </button>
        <button 
          onClick={() => setFilter('Paid')} 
          style={{...styles.tab, background: filter === 'Paid' ? '#60b246' : '#e0e0e0'}}
        >
          💰 Paid ({requests.filter(r => r.status === 'Paid').length})
        </button>
        <button 
          onClick={() => setFilter('Rejected')} 
          style={{...styles.tab, background: filter === 'Rejected' ? '#dc143c' : '#e0e0e0'}}
        >
          ❌ Rejected ({requests.filter(r => r.status === 'Rejected').length})
        </button>
        <button 
          onClick={() => setFilter('all')} 
          style={{...styles.tab, background: filter === 'all' ? '#666' : '#e0e0e0'}}
        >
          All ({requests.length})
        </button>
      </div>

      {/* Requests List */}
      <div style={styles.requestsList}>
        {filteredRequests.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No {filter !== 'all' ? filter.toLowerCase() : ''} payment requests</p>
          </div>
        ) : (
          filteredRequests.map((request) => (
            <div key={request._id} style={styles.requestCard}>
              {/* Partner Info */}
              <div style={styles.partnerInfo}>
                <h3 style={{margin: 0, color: '#282c3f'}}>{request.partnerName}</h3>
                <p style={{margin: '5px 0', color: '#686b78', fontSize: '14px'}}>
                  📱 {request.partnerPhone} • 🚴 {request.partnerVehicle}
                </p>
              </div>

              {/* Request Details */}
              <div style={styles.requestDetails}>
                <div style={styles.amountSection}>
                  <p style={{margin: 0, fontSize: '14px', color: '#686b78'}}>Requested Amount</p>
                  <p style={{margin: '5px 0 0', fontSize: '32px', fontWeight: 'bold', color: '#60b246'}}>
                    ₹{request.amount}
                  </p>
                </div>

                <div style={styles.infoGrid}>
                  <div>
                    <p style={styles.infoLabel}>Requested On</p>
                    <p style={styles.infoValue}>
                      {new Date(request.requestedAt).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div>
                    <p style={styles.infoLabel}>Status</p>
                    <span style={{
                      ...styles.statusBadge,
                      background: 
                        request.status === 'Paid' ? '#60b246' :
                        request.status === 'Approved' ? '#5d8ed5' :
                        request.status === 'Rejected' ? '#dc143c' :
                        '#ffa500'
                    }}>
                      {request.status}
                    </span>
                  </div>
                </div>

                {request.processedAt && (
                  <div style={{marginTop: '15px'}}>
                    <p style={styles.infoLabel}>Processed On</p>
                    <p style={styles.infoValue}>
                      {new Date(request.processedAt).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}

                {request.notes && (
                  <div style={{marginTop: '15px', padding: '15px', background: '#f9f9f9', borderRadius: '8px'}}>
                    <p style={styles.infoLabel}>Notes</p>
                    <p style={{margin: '5px 0 0', color: '#282c3f'}}>{request.notes}</p>
                  </div>
                )}
              </div>

              {/* Bank Account Info */}
              {request.bankAccount && (
                <div style={styles.bankDetails}>
                  <h4 style={{margin: '0 0 10px', color: '#282c3f'}}>🏦 Bank Account Details</h4>
                  <div style={styles.bankGrid}>
                    <div>
                      <p style={styles.infoLabel}>Account Holder</p>
                      <p style={styles.infoValue}>{request.bankAccount.accountHolderName || 'N/A'}</p>
                    </div>
                    <div>
                      <p style={styles.infoLabel}>Account Number</p>
                      <p style={styles.infoValue}>{request.bankAccount.accountNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <p style={styles.infoLabel}>IFSC Code</p>
                      <p style={styles.infoValue}>{request.bankAccount.ifscCode || 'N/A'}</p>
                    </div>
                    <div>
                      <p style={styles.infoLabel}>Bank Name</p>
                      <p style={styles.infoValue}>{request.bankAccount.bankName || 'N/A'}</p>
                    </div>
                    {request.bankAccount.upiId && (
                      <div>
                        <p style={styles.infoLabel}>UPI ID</p>
                        <p style={styles.infoValue}>{request.bankAccount.upiId}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={styles.actions}>
                {request.status === 'Pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(request._id, request.partnerId)}
                      style={{...styles.actionBtn, background: '#60b246'}}
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => handleReject(request._id, request.partnerId)}
                      style={{...styles.actionBtn, background: '#dc143c'}}
                    >
                      ❌ Reject
                    </button>
                  </>
                )}
                {request.status === 'Approved' && (
                  <button
                    onClick={() => handleMarkPaid(request._id, request.partnerId)}
                    style={{...styles.actionBtn, background: '#5d8ed5'}}
                  >
                    💰 Mark as Paid
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const styles: any = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    fontSize: '32px',
    color: '#282c3f',
    marginBottom: '30px',
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#282c3f',
    transition: 'all 0.3s',
  },
  requestsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  requestCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    border: '2px solid #e0e0e0',
  },
  partnerInfo: {
    paddingBottom: '20px',
    borderBottom: '2px solid #f0f0f0',
    marginBottom: '20px',
  },
  requestDetails: {
    marginBottom: '20px',
  },
  amountSection: {
    background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
  },
  infoLabel: {
    margin: 0,
    fontSize: '13px',
    color: '#686b78',
    fontWeight: '500',
  },
  infoValue: {
    margin: '5px 0 0',
    fontSize: '16px',
    color: '#282c3f',
    fontWeight: '600',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: 'white',
  },
  bankDetails: {
    background: '#f9f9f9',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  bankGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
  },
  actions: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap',
  },
  actionBtn: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: 'white',
    cursor: 'pointer',
    flex: 1,
    minWidth: '150px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#686b78',
    fontSize: '18px',
  },
};

export default PaymentRequestsManagement;
